import React, { useState } from 'react';
import { X, ExternalLinkIcon } from 'lucide-react';
export interface AdBannerData {
  id: string;
  label: string;
  title: string;
  description: string;
  ctaText: string;
  ctaUrl?: string;
  onCtaClick?: () => void;
  bgColor?: string;
  accentColor?: string;
  imageUrl?: string;
  sponsor?: string;
}
interface AdBannerProps {
  ad: AdBannerData;
  variant?: 'inline' | 'sidebar';
  className?: string;
}
export function AdBanner({
  ad,
  variant = 'inline',
  className = ''
}: AdBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  if (variant === 'sidebar') {
    return <div className={`relative rounded-2xl overflow-hidden border border-slate-200 ${className}`} data-id="element-13383">
        {/* Ad label */}
        <div className="absolute top-3 left-3 z-10" data-id="element-13384">
          <span className="px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-slate-500 uppercase tracking-wider rounded-md" data-id="element-13385">
            {ad.label}
          </span>
        </div>
        {/* Close */}
        <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm text-slate-400 hover:text-slate-600 transition-colors" data-id="element-13386">
          <X className="w-3.5 h-3.5" data-id="element-13387" />
        </button>

        {/* Image area */}
        {ad.imageUrl ? <div className="w-full h-40 overflow-hidden" data-id="element-13388">
            <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" data-id="element-13389" />
          </div> : <div className={`w-full h-32 ${ad.bgColor || 'bg-gradient-to-br from-indigo-500 to-purple-600'} flex items-center justify-center`} data-id="element-13390">
            <span className="text-white/20 text-5xl font-black" data-id="element-13391">AD</span>
          </div>}

        <div className="p-5" data-id="element-13392">
          {ad.sponsor && <p className="text-xs text-slate-400 mb-2" data-id="element-13393">{ad.sponsor}</p>}
          <h4 className="font-bold text-slate-900 text-sm mb-2 leading-snug" data-id="element-13394">
            {ad.title}
          </h4>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed" data-id="element-13395">
            {ad.description}
          </p>
          <button onClick={ad.onCtaClick} className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 ${ad.accentColor || 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-13396">
            {ad.ctaText}
          </button>
        </div>
      </div>;
  }
  // Inline variant (horizontal, between content sections)
  return <div className={`relative my-10 rounded-2xl overflow-hidden border border-slate-200 bg-white ${className}`} data-id="element-13397">
      {/* Ad label + close */}
      <div className="absolute top-3 left-4 z-10" data-id="element-13398">
        <span className="px-2 py-0.5 bg-slate-100 text-[10px] font-semibold text-slate-400 uppercase tracking-wider rounded-md" data-id="element-13399">
          {ad.label}
        </span>
      </div>
      <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-13400">
        <X className="w-4 h-4" data-id="element-13401" />
      </button>

      <div className="flex flex-col sm:flex-row" data-id="element-13402">
        {/* Image / Visual */}
        {ad.imageUrl ? <div className="sm:w-64 h-48 sm:h-auto shrink-0 overflow-hidden" data-id="element-13403">
            <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" data-id="element-13404" />
          </div> : <div className={`sm:w-56 h-40 sm:h-auto shrink-0 ${ad.bgColor || 'bg-gradient-to-br from-indigo-500 to-purple-600'} flex items-center justify-center`} data-id="element-13405">
            <span className="text-white/20 text-6xl font-black" data-id="element-13406">✦</span>
          </div>}

        {/* Content */}
        <div className="flex-1 p-6 sm:p-8 flex flex-col justify-center" data-id="element-13407">
          {ad.sponsor && <p className="text-xs text-slate-400 mb-1.5 font-medium" data-id="element-13408">
              {ad.sponsor}
            </p>}
          <h3 className="font-bold text-slate-900 text-lg sm:text-xl mb-2 leading-snug" data-id="element-13409">
            {ad.title}
          </h3>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed max-w-lg" data-id="element-13410">
            {ad.description}
          </p>
          <div data-id="element-13411">
            <button onClick={ad.onCtaClick} className={`inline-flex items-center gap-2 py-2.5 px-6 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg ${ad.accentColor || 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-13412">
              {ad.ctaText}
              <ExternalLinkIcon className="w-4 h-4" data-id="element-13413" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Default ad banners (can be configured from admin or API)
export const DEFAULT_ARTICLE_ADS: AdBannerData[] = [{
  id: 'natal-promo',
  label: 'Реклама',
  sponsor: 'AstroGuide',
  title: 'Узнайте всё о себе по натальной карте',
  description: 'Персональный PDF-отчёт на 40+ страниц: характер, таланты, карьера, отношения, финансы. Точные расчёты по швейцарским эфемеридам.',
  ctaText: 'Заказать отчёт',
  bgColor: 'bg-gradient-to-br from-violet-500 to-indigo-600',
  accentColor: 'bg-indigo-600 hover:bg-indigo-700'
}, {
  id: 'consultation-promo',
  label: 'Реклама',
  sponsor: 'AstroGuide',
  title: 'Консультация с профессиональным астрологом',
  description: 'Живой разговор с проверенным специалистом. Ответы на ваши вопросы о карьере, отношениях, переездах и важных решениях.',
  ctaText: 'Выбрать астролога',
  bgColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
  accentColor: 'bg-amber-600 hover:bg-amber-700'
}, {
  id: 'solar-promo',
  label: 'Реклама',
  sponsor: 'AstroGuide',
  title: 'Прогноз на ваш персональный год',
  description: 'Соляр (солнечное возвращение) — узнайте ключевые темы, возможности и вызовы ближайшего года. Подробный PDF-отчёт.',
  ctaText: 'Узнать подробнее',
  bgColor: 'bg-gradient-to-br from-emerald-500 to-teal-600',
  accentColor: 'bg-emerald-600 hover:bg-emerald-700'
}];